import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { deleteUserFromGroup, sendLaunchEmails, sendReminderEmails, updateUser, FRONT_URL } from "../../axios/axiosFunctions";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EmailIcon from '@mui/icons-material/Email';

const defaultUser = {
    firstName: "",
    lastName:"",
    email: "",
}

const Actions360 = ({row,api}) => {

    const {slugAssessment} = useParams();
    const [openDialogEdit, setOpenDialogEdit] = useState(false);
    const [openDialogDelete, setOpenDialogDelete] = useState(false);
    const [openDialogEmail, setOpenDialogEmail] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [emailResult, setEmailResult] = useState(null);
    const [alertSeverity, setAlertSeverity] = useState("error");
    const [alertMsg, setAlertMsg] = useState("");
    const [alertOpen, setAlertOpen] = useState(false);
    const [userForm, setUserForm] = useState(defaultUser);
    const { firstName, lastName, email } = userForm;

    useEffect(() => {
        initUser();
    }, [])

    const initUser = ()=>{
        setUserForm({
            firstName: row.firstName || "",
            lastName: row.lastName || "",
            email: row.email || "",
        });
    }

    const handleCloseDialogEdit = ()=>{
        setOpenDialogEdit(false);
    }

    const handleCloseDialogDelete = ()=>{
        setOpenDialogDelete(false);
    }

    const handleEdit = ()=>{
        initUser();
        setOpenDialogEdit(true);
    }

    const handleOpenDelete = ()=>{
        setOpenDialogDelete(true);
    }

    const handleChange = (e)=>{
        const {name,value} = e.target;
        setUserForm({...userForm,[name]:value});
    }

    const handleCancelEdit = ()=>{
        initUser();
        handleCloseDialogEdit();
    }
    
    const submitUser = async()=>{
        if(!firstName || !lastName || !email){
            setAlertMsg("All fields are required.");
            setAlertSeverity("error");
            setAlertOpen(true);
            setTimeout(() => {
              setAlertOpen(false);
            }, 3000);
            return;
        }

        const dataUser = {
            firstName,
            lastName,
            email
        }

        const resUser = await updateUser(row.userId,dataUser);

        if(!resUser.user){
            setAlertMsg(resUser.msg);
            setAlertSeverity("error");
            setAlertOpen(true);
            setTimeout(() => {
              setAlertOpen(false);
            }, 2000);
            return;
        }

        api.updateRows([{id:row.id,firstName,lastName,email}]);
        handleCloseDialogEdit();
    }

    const deleteUser = async()=>{
        const data = {
            is360: true
        };    
        const res = await deleteUserFromGroup(row.groupId,row.userId,data);
        if(!res){
            setAlertMsg("Error while deleting, please try later or contact an admin");
            setAlertSeverity("error");
            setAlertOpen(true);
            setTimeout(() => {
              setAlertOpen(false);
            }, 2000);
            return;
        }
        row.updateTable();
        row.callGroups();
        handleCloseDialogDelete();
    }

    const handleSendEmail = async (revieweeOnly = false) => {
        setSendingEmail(true);
        setEmailResult(null);
        try {
            const res = await sendLaunchEmails(row.groupId, {
                slug: slugAssessment,
                frontUrl: FRONT_URL,
                revieweeIds: [row.userId],
                revieweeOnly,
            });
            if (revieweeOnly) {
                setEmailResult({ severity: "success", msg: `Reminder sent to ${row.firstName} ${row.lastName} (${row.email}).` });
            } else {
                setEmailResult({ severity: "success", msg: `Email sent to ${row.email}. Reviewee: ${res.revieweeSent || 0}, Reviewers: ${res.reviewerSent || 0}` });
            }
        } catch (err) {
            setEmailResult({ severity: "error", msg: "Failed to send email. Please try again." });
        }
        setSendingEmail(false);
    };

    const handleOpenReviewers = ()=>{
        row.setUser(row.userId,row.groupId);
    }

    return (
    <Box sx={{width: "100%",height: "100%",display: "flex",justifyContent: "space-evenly", alignItems: "center"}}>
        <Tooltip title="Send email to this reviewee & their reviewers">
            <IconButton onClick={() => setOpenDialogEmail(true)} size="small">
                <EmailIcon sx={{color:"#1976d2",fontSize:"20px"}}/>
            </IconButton>
        </Tooltip>
        <IconButton onClick={handleEdit}>
            <EditIcon sx={{color:"orange"}}/>
        </IconButton>
        <IconButton onClick={handleOpenReviewers}>
            <AddCircleOutlineIcon/>
        </IconButton>
        <IconButton onClick={handleOpenDelete}>
            <DeleteIcon sx={{color:"red"}}/>
        </IconButton>
        <Dialog open={openDialogEmail} onClose={() => { if (!sendingEmail) { setOpenDialogEmail(false); setEmailResult(null); } }}>
            <DialogTitle sx={{ fontWeight: "bold" }}>Send Email</DialogTitle>
            <DialogContent>
                <Typography sx={{ mb: 2 }}>
                    Send email to <strong>{row.firstName} {row.lastName}</strong> ({row.email})
                </Typography>
                {emailResult && (
                    <Alert severity={emailResult.severity} sx={{ mt: 1 }}>{emailResult.msg}</Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: "16px", flexDirection: "column", gap: 1, alignItems: "stretch" }}>
                {!emailResult ? (
                    <>
                        <Button onClick={() => handleSendEmail(true)} variant="outlined" color="primary" disabled={sendingEmail}
                            startIcon={sendingEmail ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}>
                            Send to Reviewee Only
                        </Button>
                        <Button onClick={() => handleSendEmail(false)} variant="contained" color="primary" disabled={sendingEmail}
                            startIcon={sendingEmail ? <CircularProgress size={18} color="inherit" /> : <EmailIcon />}>
                            Send to Reviewee & Reviewers
                        </Button>
                        <Button onClick={() => { setOpenDialogEmail(false); setEmailResult(null); }} disabled={sendingEmail} color="inherit">Cancel</Button>
                    </>
                ) : (
                    <Button onClick={() => { setOpenDialogEmail(false); setEmailResult(null); }} variant="contained">Done</Button>
                )}
            </DialogActions>
        </Dialog>
        <Dialog open={openDialogEdit} onClose={handleCloseDialogEdit}>
            <DialogContent>
                <Typography variant="h5" color="primary">EDIT</Typography>
                <Grid container spacing={2}>
                    <Grid size={6}>
                        <Typography variant="subtitle1">First Name</Typography>
                        <TextField onChange={handleChange} value={firstName} name="firstName" fullWidth/>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="subtitle1">Last Name</Typography>
                        <TextField onChange={handleChange} value={lastName} name="lastName" fullWidth/>
                    </Grid>
                    <Grid size={6}>
                        <Typography variant="subtitle1">Email</Typography>
                        <TextField onChange={handleChange} value={email} name="email" fullWidth/>
                    </Grid>
                    {
                        alertOpen &&
                        <Grid size={12}>
                            <Alert sx={{marginTop:"20px"}} severity={alertSeverity}>
                                {alertMsg}
                            </Alert>
                        </Grid>
                    }
                    <Grid size={12} sx={{display:"flex",justifyContent:"center"}}>
                        <Button variant="contained" onClick={submitUser}>SAVE</Button>
                        <Button variant="contained" onClick={handleCancelEdit} sx={{marginLeft:"20px"}}>CANCEL</Button>
                    </Grid>
                </Grid>
            </DialogContent>
        </Dialog>
        <Dialog open={openDialogDelete} onClose={handleCloseDialogDelete}>
            <DialogContent>
                <Typography>Are you sure you want to DELETE this user?.</Typography>
                {
                    alertOpen &&
                    <Grid size={12}>
                        <Alert sx={{marginTop:"20px"}} severity={alertSeverity}>
                            {alertMsg}
                        </Alert>
                    </Grid>
                }
                <Box sx={{display:"flex",justifyContent:"center",marginTop:"20px"}}>
                    <Button onClick={deleteUser} variant="contained" color="error">DELETE</Button>
                    <Button variant="contained" onClick={handleCloseDialogDelete} sx={{marginLeft:"20px"}}>CANCEL</Button>
                </Box>
            </DialogContent>
        </Dialog>
    </Box>
    )
}

export default Actions360